import { useState } from "react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { PenLine, Loader2 } from "lucide-react";

const ENTRY_TYPES = [
  { v: "loan_received",        l: "Loan Received" },
  { v: "director_loan",        l: "Director Loan" },
  { v: "cash_payment",         l: "Cash Payment" },
  { v: "owner_injection",      l: "Owner Capital Injection" },
  { v: "refund",               l: "Refund" },
  { v: "correction",           l: "Correction" },
  { v: "unpaid_bill",          l: "Unpaid Bill" },
  { v: "tax_estimate",         l: "Tax Estimate" },
  { v: "customer_deposit",     l: "Customer Deposit (off-bank)" },
  { v: "supplier_commitment",  l: "Supplier Commitment" },
  { v: "adjustment",           l: "Internal Adjustment" },
  { v: "other",                l: "Other" },
];

const VAULTS = [
  { v: "TAX_CUSTODY",         l: "Tax Custody" },
  { v: "SUPPLIER_OBLIGATION", l: "Supplier Obligation" },
  { v: "CUSTOMER_TRUST",      l: "Customer Trust" },
  { v: "INVENTORY_CUSTODY",   l: "Inventory Custody" },
  { v: "OPERATING_CUSTODY",   l: "Operating Custody" },
  { v: "FREE_EQUITY",         l: "Free Equity" },
];

export function ManualEntryModal({ open, onOpenChange, onSubmit, submitting }) {
  const [form, setForm] = useState({
    entry_type: "loan_received",
    date: new Date().toISOString().slice(0, 10),
    description: "",
    counterparty: "",
    reference: "",
    amount: "",
    currency: "GBP",
    direction: "inflow",
    suggested_vault: "",
    reason: "",
    supporting_note: "",
  });
  const [errors, setErrors] = useState({});

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.description?.trim()) e.description = "Required";
    if (!form.amount || parseFloat(form.amount) <= 0) e.amount = "Must be > 0";
    if (!form.reason?.trim()) e.reason = "Required — explain why this is being entered manually";
    if (!form.date) e.date = "Required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    const payload = {
      ...form,
      amount: parseFloat(form.amount),
      counterparty: form.counterparty || null,
      reference: form.reference || null,
      suggested_vault: form.suggested_vault || null,
      supporting_note: form.supporting_note || null,
    };
    const ok = await onSubmit(payload);
    if (ok) {
      // reset for next entry
      setForm(f => ({ ...f, description: "", counterparty: "", reference: "", amount: "", reason: "", supporting_note: "" }));
      setErrors({});
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A1421] border-slate-800 max-w-2xl" data-testid="manual-entry-modal">
        <DialogHeader>
          <DialogTitle className="text-slate-100 flex items-center gap-2">
            <PenLine className="w-4 h-4 text-cyan-400" /> Add Manual Financial Entry
          </DialogTitle>
          <p className="text-xs text-slate-400 mt-1">
            Record a transaction not captured by bank, accounting, or upload sources. Manual entries
            enter the same pipeline (normalize → diagnose → review → commit → ledger) and are clearly
            labelled as <span className="text-amber-400">Source: Manual Entry</span>.
          </p>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 mt-2">
          {/* Entry type */}
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Entry type</Label>
            <Select value={form.entry_type} onValueChange={(v) => update("entry_type", v)}>
              <SelectTrigger className="bg-slate-900/60 border-slate-700 text-slate-200 h-9 mt-1" data-testid="me-entry-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700">
                {ENTRY_TYPES.map(t => <SelectItem key={t.v} value={t.v} className="text-slate-200">{t.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Date */}
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Date</Label>
            <Input type="date" value={form.date} onChange={(e) => update("date", e.target.value)}
              className="bg-slate-900/60 border-slate-700 text-slate-200 h-9 mt-1" data-testid="me-date" />
            {errors.date && <p className="text-[10px] text-red-400 mt-0.5">{errors.date}</p>}
          </div>

          {/* Description (full width) */}
          <div className="col-span-2">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Description</Label>
            <Input value={form.description} onChange={(e) => update("description", e.target.value)}
              placeholder="e.g. Director loan from John Smith"
              className="bg-slate-900/60 border-slate-700 text-slate-200 h-9 mt-1" data-testid="me-description" />
            {errors.description && <p className="text-[10px] text-red-400 mt-0.5">{errors.description}</p>}
          </div>

          {/* Counterparty + Reference */}
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Counterparty (optional)</Label>
            <Input value={form.counterparty} onChange={(e) => update("counterparty", e.target.value)}
              placeholder="Name of party"
              className="bg-slate-900/60 border-slate-700 text-slate-200 h-9 mt-1" data-testid="me-counterparty" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Reference (optional)</Label>
            <Input value={form.reference} onChange={(e) => update("reference", e.target.value)}
              placeholder="Invoice / loan / cheque ref"
              className="bg-slate-900/60 border-slate-700 text-slate-200 h-9 mt-1" data-testid="me-reference" />
          </div>

          {/* Amount + Currency + Direction */}
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Amount</Label>
            <Input type="number" step="0.01" min="0" value={form.amount}
              onChange={(e) => update("amount", e.target.value)}
              placeholder="1000.00"
              className="bg-slate-900/60 border-slate-700 text-slate-200 h-9 mt-1" data-testid="me-amount" />
            {errors.amount && <p className="text-[10px] text-red-400 mt-0.5">{errors.amount}</p>}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-500">Currency</Label>
              <Select value={form.currency} onValueChange={(v) => update("currency", v)}>
                <SelectTrigger className="bg-slate-900/60 border-slate-700 text-slate-200 h-9 mt-1" data-testid="me-currency">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  {["GBP","USD","EUR","AED","CAD","AUD","CHF","JPY","INR"].map(c => (
                    <SelectItem key={c} value={c} className="text-slate-200">{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-500">Direction</Label>
              <Select value={form.direction} onValueChange={(v) => update("direction", v)}>
                <SelectTrigger className="bg-slate-900/60 border-slate-700 text-slate-200 h-9 mt-1" data-testid="me-direction">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  <SelectItem value="inflow" className="text-emerald-400">Inflow (money in)</SelectItem>
                  <SelectItem value="outflow" className="text-red-400">Outflow (money out)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Suggested vault */}
          <div className="col-span-2">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Suggested vault (optional — AI may override)</Label>
            <Select value={form.suggested_vault || "auto"} onValueChange={(v) => update("suggested_vault", v === "auto" ? "" : v)}>
              <SelectTrigger className="bg-slate-900/60 border-slate-700 text-slate-200 h-9 mt-1" data-testid="me-suggested-vault">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700">
                <SelectItem value="auto" className="text-slate-400">Auto (let the engine decide)</SelectItem>
                {VAULTS.map(v => <SelectItem key={v.v} value={v.v} className="text-slate-200">{v.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Reason (mandatory) */}
          <div className="col-span-2">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Reason for manual entry <span className="text-red-400">*</span></Label>
            <Textarea value={form.reason} onChange={(e) => update("reason", e.target.value)}
              placeholder="e.g. Cash payment not visible on bank feed; estimated tax liability for Q1"
              className="bg-slate-900/60 border-slate-700 text-slate-200 mt-1" rows={2} data-testid="me-reason" />
            {errors.reason && <p className="text-[10px] text-red-400 mt-0.5">{errors.reason}</p>}
          </div>

          {/* Supporting note (optional) */}
          <div className="col-span-2">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Supporting note (optional)</Label>
            <Textarea value={form.supporting_note} onChange={(e) => update("supporting_note", e.target.value)}
              placeholder="Additional context, repayment terms, conditions…"
              className="bg-slate-900/60 border-slate-700 text-slate-200 mt-1" rows={2} data-testid="me-supporting-note" />
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-slate-800">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}
            className="border-slate-700 text-slate-300 hover:bg-slate-800" data-testid="me-cancel-btn">
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting}
            className="bg-cyan-600 hover:bg-cyan-500 text-white gap-2" data-testid="me-submit-btn">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <PenLine className="w-4 h-4" />}
            {submitting ? "Adding…" : "Add Manual Entry"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
